<template>
  <div class="fb-comments-container w-full h-full flex flex-col">
    <!-- Loading State (initial load only in self-fetch mode) -->
    <div v-if="effectiveLoading" class="flex-grow flex items-center justify-center">
      <BaseLoadingLogo />
    </div>

    <!-- Empty State -->
    <div v-else-if="!displayComments.length" class="flex-grow flex flex-col items-center justify-center px-6">
      <div class="w-16 h-16 rounded-full bg-neutral-25 flex items-center justify-center mb-4">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="text-text-subdued">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      </div>
      <BaseText type="label" size="sm" class="text-text-muted mb-1">
        No Comments Yet
      </BaseText>
      <BaseText type="body" size="sm" class="text-text-normal text-center">
        This ad hasn't received any comments.
      </BaseText>
    </div>

    <!-- Comments List -->
    <template v-else>
      <!-- Header with Export and Sort (sticky) -->
      <div class="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-border-normal bg-white">
        <BaseText type="label" size="sm" class="text-text-muted">
          {{ displayTotal }} {{ displayTotal === 1 ? 'Comment' : 'Comments' }}
        </BaseText>
        <div class="flex items-center gap-2">
          <!-- Export CSV Button -->
          <button 
            class="ghost-btn group flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md"
            @click="exportToCSV"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" 
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              class="text-icon-normal">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <BaseText type="label" size="sm" class="text-text-muted">
              Export CSV
            </BaseText>
          </button>
          <!-- Sort Dropdown -->
          <div class="relative" v-on-clickaway="() => showSortDropdown = false">
            <button 
              class="ghost-btn group flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md"
              :class="{ 'expanded': showSortDropdown }"
              @click="showSortDropdown = !showSortDropdown"
            >
              <SortIcon :width="18" :height="18" class="text-icon-normal" />
              <BaseText type="label" size="sm" class="text-text-muted">
                {{ currentSortLabel }}
              </BaseText>
            </button>
            <!-- Sort Dropdown Menu -->
            <transition name="fade">
              <div v-if="showSortDropdown" class="sort-dropdown flex flex-col gap-0.5 p-1 rounded-lg bg-white">
                <button 
                  v-for="option in sortOptions" 
                  :key="option.value"
                  class="dropdown-item"
                  :class="{ 'selected': sortBy === option.value }"
                  @click="setSortBy(option.value)"
                >
                  <BaseText type="body" size="sm" class="text-text-normal">
                    {{ option.name }}
                  </BaseText>
                  <NewCheckmarkIcon v-if="sortBy === option.value" class="ml-auto text-icon-normal" />
                </button>
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Scrollable Comments Container -->
      <div class="comments-list flex-grow min-h-0 overflow-y-auto p-4 pb-20 space-y-3" ref="commentsList">
        <div 
          v-for="(comment, index) in displayComments" 
          :key="comment.id || `comment-${index}`"
          class="comment-item group"
        >
          <!-- Comment Bubble (full width, no avatar) -->
          <div class="comment-bubble w-full">
            <div class="bg-neutral-25 rounded-xl px-4 py-3">
              <!-- Comment Text -->
              <BaseText type="body" size="md" class="text-text-muted break-words">
                {{ comment.text }}
              </BaseText>
              <!-- Timestamp and Likes Row (inside bubble) -->
              <div class="flex items-center gap-2 mt-2">
                <!-- Timestamp Pill -->
                <BaseTooltip dark placement="top">
                  <div slot="reference" class="meta-pill meta-pill-hover flex items-center px-1.5 py-0.5 rounded-full bg-white cursor-default">
                    <BaseText type="body" size="xs" class="text-text-subdued">
                      {{ formatTimeAgo(comment.timestamp) }}
                    </BaseText>
                  </div>
                  <div slot="content" class="px-2 py-1 whitespace-nowrap">
                    <BaseText type="body" size="xs" class="text-white">
                      {{ formatExactTimestamp(comment.timestamp) }}
                    </BaseText>
                  </div>
                </BaseTooltip>
                <!-- Like Count Pill -->
                <BaseTooltip v-if="comment.likeCount > 0" dark placement="top">
                  <div slot="reference" class="meta-pill meta-pill-hover flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-full bg-white cursor-default">
                    <div class="like-icon-wrapper w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="white">
                        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                      </svg>
                    </div>
                    <BaseText type="body" size="xs" class="text-text-subdued">
                      {{ formatLikeCount(comment.likeCount) }}
                    </BaseText>
                  </div>
                  <div slot="content" class="px-2 py-1 whitespace-nowrap">
                    <BaseText type="body" size="xs" class="text-white">
                      {{ comment.likeCount }} Comment {{ comment.likeCount === 1 ? 'Like' : 'Likes' }}
                    </BaseText>
                  </div>
                </BaseTooltip>
                <!-- User Post Like Pill -->
                <BaseTooltip v-if="typeof comment.userPostLike === 'boolean'" dark placement="top">
                  <div slot="reference" class="meta-pill meta-pill-hover flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-full bg-white cursor-default">
                    <div 
                      class="like-icon-wrapper w-4 h-4 rounded-full flex items-center justify-center"
                      :class="comment.userPostLike ? 'bg-green-500' : 'bg-neutral-300'"
                    >
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="10" 
                        height="10" 
                        viewBox="0 0 24 24" 
                        fill="white"
                        :class="{ 'transform rotate-180': !comment.userPostLike }"
                      >
                        <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                      </svg>
                    </div>
                    <BaseText type="body" size="xs" class="text-text-subdued">
                      User Post Like
                    </BaseText>
                  </div>
                  <div slot="content" class="px-2 py-1 whitespace-nowrap">
                    <BaseText type="body" size="xs" class="text-white">
                      If this commenter also liked the post
                    </BaseText>
                  </div>
                </BaseTooltip>
                <!-- Copy Button (appears on hover) -->
                <button 
                  class="copy-btn p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity ml-auto"
                  v-clipboard:copy="comment.text"
                  v-clipboard:success="() => handleCopySuccess()"
                  title="Copy comment"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" 
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    class="text-text-subdued hover:text-text-muted transition-colors">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
        <!-- Infinite scroll trigger (self-fetch mode only) -->
        <infinite-loading
          v-if="useSelfFetch && hasMorePages"
          :identifier="infiniteId"
          @infinite="loadMoreComments"
        >
          <template slot="spinner">
            <div class="flex justify-center py-4">
              <BaseLoadingSpinnerCircle class="text-icon-disabled" :width="20" :height="20" />
            </div>
          </template>
          <template slot="no-more"><span /></template>
          <template slot="no-results"><span /></template>
        </infinite-loading>
      </div>
    </template>
  </div>
</template>

<script>
import TimeAgo from 'javascript-time-ago'
import en from 'javascript-time-ago/locale/en.json'
import { mixin as clickaway } from 'vue-clickaway2'
import InfiniteLoading from 'vue-infinite-loading'

import LensAPI from '@/api/lens'
// Icons
import SortIcon from '../../globals/Icons/SortIcon.vue'
import NewCheckmarkIcon from '../../globals/Icons/NewCheckmarkIcon.vue'
import BaseLoadingSpinnerCircle from '../../globals/BaseLoadingSpinnerCircle.vue'

// API sort params (same as LensCommentsView)
const SORT_MAP = Object.freeze({
  mostLikes: { sort_by: 'like_count', sort_order: 'desc' },
  newest: { sort_by: 'created_time', sort_order: 'desc' },
  oldest: { sort_by: 'created_time', sort_order: 'asc' }
})

export default {
  name: 'FBComments',
  mixins: [clickaway],
  components: {
    SortIcon,
    NewCheckmarkIcon,
    InfiniteLoading,
    BaseLoadingSpinnerCircle
  },
  props: {
    advertisement: {
      type: Object,
      default: () => ({})
    },
    /** When provided with advertisement.ad_id, component fetches comments via API with pagination and sort */
    lensId: {
      type: String,
      default: null
    },
    fbComments: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    usePlaceholders: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      timeAgo: null,
      // Self-fetch state (when lensId + advertisement.ad_id are set)
      internalComments: [],
      totalComments: 0,
      totalPages: 1,
      currentPage: 1,
      limit: 20,
      internalLoading: false,
      loadingMore: false,
      infiniteId: 0,
      // Placeholder comments for development
      placeholderComments: [
        {
          authorName: 'Sarah Johnson',
          authorAvatar: null,
          text: 'This is exactly what I\'ve been looking for! Just ordered mine 🙌',
          timestamp: Date.now() - 1000 * 60 * 30, // 30 minutes ago
          likeCount: 24,
          userPostLike: true
        },
        {
          authorName: 'Mike Chen',
          authorAvatar: null,
          text: 'Does anyone know if this ships internationally?',
          timestamp: Date.now() - 1000 * 60 * 60 * 2, // 2 hours ago
          likeCount: 3,
          userPostLike: false
        },
        {
          authorName: 'Emma Williams',
          authorAvatar: null,
          text: 'I bought this last month and absolutely love it. The quality is amazing and customer service was super helpful when I had questions.',
          timestamp: Date.now() - 1000 * 60 * 60 * 5, // 5 hours ago
          likeCount: 156,
          userPostLike: true
        },
        {
          authorName: 'David Park',
          authorAvatar: null,
          text: '🔥🔥🔥',
          timestamp: Date.now() - 1000 * 60 * 60 * 8, // 8 hours ago
          likeCount: 12,
          userPostLike: true
        },
        {
          authorName: 'Lisa Thompson',
          authorAvatar: null,
          text: 'Is there a discount code available?',
          timestamp: Date.now() - 1000 * 60 * 60 * 24, // 1 day ago
          likeCount: 0,
          userPostLike: false
        },
        {
          authorName: 'James Wilson',
          authorAvatar: null,
          text: 'Just saw this ad for the third time today. Taking it as a sign I need to buy 😂',
          timestamp: Date.now() - 1000 * 60 * 60 * 24 * 2, // 2 days ago
          likeCount: 89,
          userPostLike: true
        },
        {
          authorName: 'Amanda Garcia',
          authorAvatar: null,
          text: 'Can someone tell me how long shipping takes? Need it for an event next week.',
          timestamp: Date.now() - 1000 * 60 * 60 * 24 * 3, // 3 days ago
          likeCount: 2,
          userPostLike: false
        },
        {
          authorName: 'Chris Martinez',
          authorAvatar: null,
          text: 'This brand never disappoints. Been a customer for 2 years now.',
          timestamp: Date.now() - 1000 * 60 * 60 * 24 * 4, // 4 days ago
          likeCount: 47,
          userPostLike: true
        },
        {
          authorName: 'Rachel Kim',
          authorAvatar: null,
          text: 'The before and after results look incredible! Has anyone actually tried this?',
          timestamp: Date.now() - 1000 * 60 * 60 * 24 * 5, // 5 days ago
          likeCount: 8,
          userPostLike: false
        },
        {
          authorName: 'Tom Anderson',
          authorAvatar: null,
          text: 'Finally a brand that actually delivers on their promises. Highly recommend to everyone!',
          timestamp: Date.now() - 1000 * 60 * 60 * 24 * 7, // 1 week ago
          likeCount: 203,
          userPostLike: true
        }
      ],
      // Sort state
      sortBy: 'mostLikes',
      showSortDropdown: false,
      sortOptions: [
        { name: 'Most Likes', value: 'mostLikes' },
        { name: 'Newest', value: 'newest' },
        { name: 'Oldest', value: 'oldest' }
      ]
    }
  },
  computed: {
    useSelfFetch () {
      return Boolean(this.lensId && this.advertisement?.ad_id)
    },
    /** In self-fetch mode use internalComments; otherwise use prop + client sort */
    displayComments () {
      if (this.useSelfFetch) {
        return this.internalComments
      }
      let commentsData = []
      if (this.fbComments && this.fbComments.length > 0) {
        commentsData = [...this.fbComments]
      } else if (this.usePlaceholders) {
        commentsData = [...this.placeholderComments]
      }
      switch (this.sortBy) {
        case 'newest':
          return commentsData.sort((a, b) => b.timestamp - a.timestamp)
        case 'oldest':
          return commentsData.sort((a, b) => a.timestamp - b.timestamp)
        case 'mostLikes':
          return commentsData.sort((a, b) => (b.likeCount || 0) - (a.likeCount || 0))
        default:
          return commentsData
      }
    },
    displayTotal () {
      if (this.useSelfFetch) return this.totalComments
      return this.displayComments.length
    },
    effectiveLoading () {
      if (this.useSelfFetch) return this.internalLoading
      return this.loading
    },
    hasMorePages () {
      return this.useSelfFetch && this.currentPage < this.totalPages
    },
    /** For CSV export and any logic that needs the current list */
    comments () {
      return this.displayComments
    },
    currentSortLabel () {
      const option = this.sortOptions.find(o => o.value === this.sortBy)
      return option ? option.name : 'Most Likes'
    }
  },
  watch: {
    lensId: { handler () { this.resetAndFetchIfSelfFetch() } },
    'advertisement.ad_id': { handler () { this.resetAndFetchIfSelfFetch() } }
  },
  mounted () {
    try {
      TimeAgo.addDefaultLocale(en)
    } catch (e) {
      // Locale may already be added
    }
    this.timeAgo = new TimeAgo('en-US')
    if (this.useSelfFetch) {
      this.fetchComments(false)
    }
  },
  methods: {
    resetAndFetchIfSelfFetch () {
      if (!this.useSelfFetch) return
      this.currentPage = 1
      this.internalComments = []
      this.totalComments = 0
      this.totalPages = 1
      this.infiniteId += 1
      this.fetchComments(false)
    },
    async fetchComments (append = false) {
      if (!this.useSelfFetch) return
      const adId = this.advertisement?.ad_id
      if (!this.lensId || !adId) return

      if (!append) {
        this.internalLoading = true
      } else {
        this.loadingMore = true
      }

      try {
        const sortParams = SORT_MAP[this.sortBy] || SORT_MAP.mostLikes
        const pageToFetch = append ? this.currentPage + 1 : 1
        const requestParams = {
          limit: this.limit,
          page: pageToFetch,
          ...sortParams
        }
        const response = await LensAPI.Lens.getAdComments(this.lensId, adId, requestParams)

        let commentsArray = []
        if (Array.isArray(response)) {
          commentsArray = response
        } else if (response?.data && Array.isArray(response.data)) {
          commentsArray = response.data
        } else if (response?.comments && Array.isArray(response.comments)) {
          commentsArray = response.comments
        }

        const total = response?.total ?? 0
        const pages = response?.pages ?? (Math.ceil(total / this.limit) || 1)

        if (!append) {
          this.totalComments = total
          this.totalPages = pages
          this.currentPage = 1
          this.internalComments = commentsArray.map(c => this.normalizeComment(c))
          this.$emit('total', total)
        } else {
          this.internalComments.push(...commentsArray.map(c => this.normalizeComment(c)))
          this.currentPage = pageToFetch
        }
      } catch (error) {
        console.error('[FBComments] Error fetching comments:', error)
        if (!append) {
          this.internalComments = []
          this.totalComments = 0
          this.totalPages = 1
          this.$emit('total', 0)
        }
      } finally {
        this.internalLoading = false
        this.loadingMore = false
      }
    },
    loadMoreComments ($state) {
      if (!this.useSelfFetch || !this.hasMorePages || this.loadingMore) {
        if ($state) $state.loaded()
        return
      }
      this.fetchComments(true).then(() => {
        if ($state) {
          if (this.currentPage >= this.totalPages) {
            $state.complete()
          } else {
            $state.loaded()
          }
        }
      }).catch(() => {
        if ($state) $state.loaded()
      })
    },
    normalizeComment (comment) {
      const timestamp = this.parseTimestamp(comment.created_time || comment.timestamp)
      return {
        id: comment.id,
        text: comment.message || comment.text || '',
        timestamp,
        likeCount: comment.like_count ?? comment.likeCount ?? 0,
        userPostLike: comment.user_likes ?? comment.user_post_like ?? comment.userPostLike ?? false
      }
    },
    parseTimestamp (timestamp) {
      if (!timestamp) return Date.now()
      if (typeof timestamp === 'number' && timestamp < 10000000000) {
        return timestamp * 1000
      }
      return new Date(timestamp).getTime()
    },
    setSortBy (value) {
      this.sortBy = value
      this.showSortDropdown = false
      if (this.useSelfFetch) {
        this.currentPage = 1
        this.infiniteId += 1
        this.fetchComments(false)
      }
    },
    getInitials (name) {
      if (!name) return '?'
      const parts = name.split(' ')
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return name.substring(0, 2).toUpperCase()
    },
    formatTimeAgo (timestamp) {
      if (!this.timeAgo || !timestamp) return ''
      return this.timeAgo.format(timestamp)
    },
    formatExactTimestamp (timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      return date.toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    },
    handleCopySuccess () {
      this.$showAlert({
        message: 'Comment copied',
        type: 'success'
      })
    },
    formatLikeCount (count) {
      if (count >= 1000000) {
        return (count / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      }
      if (count >= 1000) {
        return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
      }
      return count.toString()
    },
    exportToCSV () {
      if (!this.comments.length) return

      // Build CSV content
      const headers = ['Author', 'Comment', 'Likes', 'User Post Like', 'Timestamp']
      const rows = this.comments.map(comment => [
        `"${(comment.authorName || '').replace(/"/g, '""')}"`,
        `"${(comment.text || '').replace(/"/g, '""')}"`,
        comment.likeCount || 0,
        comment.userPostLike === true ? 'Yes' : comment.userPostLike === false ? 'No' : '',
        `"${new Date(comment.timestamp).toISOString()}"`
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n')

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      // Sanitize ad name for filename (remove special characters)
      const adName = (this.advertisement?.name || 'export').replace(/[^a-zA-Z0-9-_]/g, '-').replace(/-+/g, '-')
      link.setAttribute('download', `${adName}-fb-comments-export.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      this.$showAlert({
        message: 'Comments exported to CSV',
        type: 'success'
      })
    }
  }
}
</script>

<style scoped>
/* Container z-index containment */
.fb-comments-container {
  position: relative;
  z-index: 0;
  isolation: isolate;
}

/* Ghost Button Style (like filter bar) */
.ghost-btn {
  background-color: transparent;
  box-shadow: none;
  transition: box-shadow 150ms ease-in-out, background-color 150ms ease-in-out;
}

.ghost-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  background-color: white;
}

.ghost-btn.expanded {
  box-shadow: none;
  background-color: #F6F8FA; /* neutral-25 */
}

.comments-list {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  position: relative;
  z-index: 0;
}

.comments-list:hover {
  scrollbar-color: #DFE1E7 transparent;
}

.comments-list::-webkit-scrollbar {
  width: 4px;
}

.comments-list::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.comments-list:hover::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
}

.comment-bubble .copy-btn {
  background-color: transparent;
  transition: background-color 150ms ease, opacity 150ms ease;
  flex-shrink: 0;
}

.comment-bubble .copy-btn:hover {
  background-color: white;
}

.meta-pill {
  border: 1px solid #ECEFF3; /* neutral-50 border */
  transition: background-color 150ms ease;
  position: relative;
  z-index: 1;
}

.meta-pill-hover:hover {
  background-color: #F6F8FA; /* neutral-25 */
}

/* Subtle tooltip transition */
.meta-pill-hover >>> .popper {
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 150ms ease, transform 150ms ease;
  z-index: 10 !important;
}

.meta-pill-hover:hover >>> .popper {
  opacity: 1;
  transform: translateY(0);
}

/* Ensure comment items don't overlap bottom bar */
.comment-item {
  position: relative;
  z-index: 1;
}

/* Sort Dropdown */
.sort-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 130px;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  z-index: 100;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.375rem;
  transition: background-color 150ms ease-in-out;
  cursor: pointer;
}

.dropdown-item:hover,
.dropdown-item.selected {
  background-color: #F8FAFB;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 150ms ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

</style>

